// src/pages/VocabularySection.jsx
//
// Student-facing render of a skills-workshop `vocabulary` lit_section (slice 3:
// Thematic Vocabulary). Mirrors TechniqueSection/AnalysisSection (pure
// presentational, takes `content` directly, neutral book-* palette) so it drops
// into the shared grouped reader the same way.
//
// Reads content.blocks: { intro, entries:[{word,definition,example,note?}], tip }
// plus the slice-2 stimulus blocks (keyIdea/image/video -- shared render helpers).
// Entries render as SCANNABLE CARDS, not prose: word in display font + accent,
// definition in serif, example sentence quoted in italic, optional small note tag.
// The unit grouping (blocks.unit -- Settings / Characters / Emotions / Common
// actions by authoring convention) is nav metadata and is NOT rendered here.
import React from 'react';
import RichText from '../components/richText/RichText.jsx';
import YouTubeEmbed, { parseYouTubeId } from '../components/YouTubeEmbed.jsx';
import { KeyIdeaBox, StimulusImage, TechniquePart } from './TechniqueSection.jsx';
import { selectRenderableParts } from './techniqueParts.js';
import LessonCard, { LessonCardHeading, ReadMore } from '../components/reader/LessonCard.jsx';
import LessonHeader from '../components/reader/LessonHeader.jsx';

function EmptyNote() {
    return <p className="font-serif italic text-book-muted text-sm">Nothing here yet.</p>;
}

export default function VocabularySection({ content, done = false, lessonOptions }) {
    const blocks = (content && content.blocks) || {};
    const keyIdea = typeof blocks.keyIdea === 'string' ? blocks.keyIdea : '';
    const intro = typeof blocks.intro === 'string' ? blocks.intro : '';
    const tip = typeof blocks.tip === 'string' ? blocks.tip : '';
    const image = (blocks.image && typeof blocks.image === 'object' && !Array.isArray(blocks.image)) ? blocks.image : null;
    const video = typeof blocks.video === 'string' ? blocks.video : '';
    const entries = Array.isArray(blocks.entries)
        ? blocks.entries.filter((e) => e && typeof e === 'object'
            && (typeof e.word === 'string' && e.word.trim()) && (typeof e.definition === 'string' || typeof e.example === 'string'))
        : [];
    // Presence flags mirror the ACTUAL render conditions (the slice-2 lesson): a
    // caption-only image or an unparseable video counts as nothing.
    const imageRenderable = !!(image && typeof image.imageUrl === 'string' && image.imageUrl.trim());
    const videoRenderable = !!parseYouTubeId(video);
    // NEW model (analysis-vocab-flexible-subheadings): the author's flexible
    // subheadings. Non-empty -> they REPLACE the fixed intro + section image/video;
    // the vocab entry cards + tip still follow.
    const parts = selectRenderableParts(blocks, parseYouTubeId);
    const empty = !keyIdea && parts.length === 0 && !intro && !imageRenderable && !videoRenderable && !entries.length && !tip;

    // max-w-2xl caps the reading measure, matching the sibling section renderers.
    return (
        <div className="max-w-2xl mx-auto pb-8">
            <LessonHeader
                eyebrow={typeof blocks.unit === 'string' && blocks.unit.trim() ? blocks.unit.trim() : 'Thematic vocabulary'}
                title={(content && content.title) || 'Vocabulary'} blocks={blocks} done={done} />

            {empty && <EmptyNote />}

            <KeyIdeaBox keyIdea={keyIdea} />

            {parts.length > 0 ? (
                parts.map((p, i) => <TechniquePart key={(typeof p.id === 'string' && p.id) || 'part' + i} part={p} missionContent={content} lessonOptions={lessonOptions} />)
            ) : (<>
                {intro && (
                    <LessonCard className="mb-4">
                        <ReadMore text={intro}>
                            <RichText text={intro} className="font-serif text-book-text text-base leading-relaxed" />
                        </ReadMore>
                    </LessonCard>
                )}

                {imageRenderable && <StimulusImage image={image} />}

                {videoRenderable && (
                    <div className="mb-7">
                        {/* Click-to-load; gated on parseYouTubeId so a bad URL leaves no spacer. */}
                        <YouTubeEmbed url={video} title={((content && content.title) || 'Vocabulary') + ' — video'} />
                    </div>
                )}
            </>)}

            {entries.length > 0 && (
                <div className="mb-4 space-y-3">
                    {entries.map((e, i) => (
                        <LessonCard key={i}>
                            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                <span className="font-display text-book-text text-lg font-bold">{e.word}</span>
                                {typeof e.note === 'string' && e.note.trim() && (
                                    <span className="font-display text-sm text-book-muted border border-book-accent/30 px-3 py-1.5 font-medium rounded-full">{e.note}</span>
                                )}
                            </div>
                            {typeof e.definition === 'string' && e.definition.trim() && (
                                <RichText text={e.definition} className="font-serif text-book-text text-sm leading-relaxed mt-1.5" />
                            )}
                            {typeof e.example === 'string' && e.example.trim() && (
                                // The quotation marks are part of the rendered string so they hug the
                                // first and last word exactly as they did inside the old <p>.
                                <RichText annotate={false} text={'\u201c' + e.example.trim() + '\u201d'} className="font-serif italic text-book-muted text-sm leading-relaxed mt-1.5" />
                            )}
                        </LessonCard>
                    ))}
                </div>
            )}

            {tip && (
                <LessonCard className="mb-4 border-l-2 border-l-book-accent">
                    <LessonCardHeading>Tip</LessonCardHeading>
                    <RichText text={tip} className="font-serif italic text-book-text text-sm leading-relaxed" />
                </LessonCard>
            )}
        </div>
    );
}
